/**
 * One product's stock ledger, newest first — the audit trail behind the
 * on-hand figure on the Stock Levels table.
 */
import Modal from 'react-modal';
import {AiOutlineClose} from 'react-icons/ai';
import {StockEvent} from '../../../internals/repository/StockEventRepository';

const EVENT_LABEL: Record<string, string> = {
  sale: 'Sale',
  refund: 'Refund',
  waste: 'Waste',
  purchase: 'Received',
  prep_set: 'Prep count',
  count_adjust: 'Counted',
  override_sale: 'Sold while out',
  transfer_out: 'Transfer out',
  transfer_in: 'Transfer in',
};

interface Props {
  productName: string;
  events: StockEvent[];
  labels?: Record<string, string>;
  onClose: () => void;
}

const StockHistoryModal = ({productName, events, onClose}: Props) => (
  <Modal
    isOpen
    onRequestClose={onClose}
    ariaHideApp={false}
    className="absolute left-1/2 top-1/2 flex max-h-[80vh] w-[560px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-surface p-0 shadow-xl outline-none"
    overlayClassName="fixed inset-0 bg-black/40 z-[1000]"
  >
    <div className="flex items-center justify-between border-b border-borderc px-5 py-4">
      <div>
        <h2 className="text-base font-semibold text-text1">Stock history</h2>
        <p className="text-xs text-text3">{productName}</p>
      </div>
      <button type="button" onClick={onClose} className="text-text3 hover:text-text1">
        <AiOutlineClose size={16} />
      </button>
    </div>

    <div className="flex-1 overflow-y-auto">
      {events.length === 0 ? (
        <p className="p-8 text-center text-sm text-text3">No stock events yet</p>
      ) : (
        <table className="w-full">
          <tbody>
            {events.map((e) => (
              <tr key={e.eventId} className="border-b border-borderc">
                <td className="px-5 py-2.5">
                  <p className="text-sm text-text1">
                    {EVENT_LABEL[e.type] ?? e.type}
                    {e.reason ? (
                      <span className="text-text3"> · {e.reason.replace(/_/g, ' ')}</span>
                    ) : null}
                  </p>
                  <p className="font-mono text-[11px] text-text3">
                    {new Date(e.createdAt).toLocaleString()}
                    {e.source ? ` · ${e.source}` : ''}
                  </p>
                </td>
                <td className="px-5 py-2.5 text-right">
                  {/* An absolute event replaced the count; a delta moved it. */}
                  <span className="font-mono text-sm font-semibold text-text1">
                    {e.absolute != null
                      ? `= ${e.absolute}`
                      : `${e.delta > 0 ? '+' : ''}${e.delta}`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </Modal>
);

export default StockHistoryModal;
